import React, { useState } from 'react';
import {
  Ship, Truck, Plane, Receipt, ShieldAlert, AlertOctagon, Tag,
  BarChart3, BookOpen, MessageSquare, Bell, Search, Download,
  CreditCard, CheckCircle2, AlertTriangle, Clock, Calendar,
  LogOut, Plus, X, ChevronRight, ArrowUp, ArrowDown, Wallet,
  Scale, TrendingUp, Target,
} from 'lucide-react';
import Badge, { TierBadge, CountBadge } from '../components/ui/Badge';
import Button, { IconButton } from '../components/ui/Button';
import { Input, Select, Textarea, FormField, ToggleGroup } from '../components/ui/Input';
import KpiCard from '../components/ui/KpiCard';
import { ShipmentCard, Card, CardRow } from '../components/ui/DataCard';
import { ShipmentTable } from '../components/ui/Table';
import { Alert, Toast } from '../components/ui/Alert';
import Drawer, { DrawerDetailGrid, DrawerProgressBar } from '../components/ui/Drawer';
import Modal from '../components/ui/Modal';
import { Avatar, AvatarGroup } from '../components/ui/Avatar';
import { Timeline } from '../components/ui/Timeline';

/* ── Section wrapper ──────────────────────────────────────── */
function Section({ id, index, title, subtitle, children }) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="mb-5">
        <div className="text-[10.5px] text-slate-400 uppercase tracking-wider font-semibold mb-1">
          {index}
        </div>
        <h2 className="text-[18px] font-semibold tracking-tight">{title}</h2>
        {subtitle && <p className="text-[13px] text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {title && (
        <div className="px-6 py-3 border-b border-slate-100">
          <span className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{title}</span>
        </div>
      )}
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

/* ── Sample data ─────────────────────────────────────────── */
const SHIPMENT_ROWS = [
  { reference: 'MFB-SH-2026-08214', client: 'Domaines Agricoles', product: 'Oranges Maroc Late',   departure: 'Agadir',      arrival: 'Rotterdam', etd: '22 avr. 2026', mode: 'reefer', status: 'transit'    },
  { reference: 'MFB-SH-2026-08210', client: 'Nile Delta Produce',  product: 'Oranges Maroc Late',   departure: 'Alexandrie',  arrival: 'St-Pét.',   etd: '18 avr. 2026', mode: 'reefer', status: 'incident'  },
  { reference: 'MFB-SH-2026-08208', client: 'Delassus',             product: 'Pastèques s. pépins', departure: 'Murcia',      arrival: 'Calais',    etd: '21 avr. 2026', mode: 'route',  status: 'delivered' },
];

const TIMELINE_ITEMS = [
  { status: 'done',    title: 'Sinistre déclaré',      date: '14 mai 2026', user: 'Brahim H.',  body: 'Rupture chaîne du froid · Rotterdam stop. Alarme +6°C pendant 4h.' },
  { status: 'active',  title: 'Investigation',          date: '16 mai 2026', user: 'MFB Ops'                                                                           },
  { status: 'pending', title: 'Expertise en attente',   date: 'Rapport CESAM Marseille attendu'                                                                        },
];

/* ── Transport icon display ──────────────────────────────── */
function TransportIcon({ mode, label }) {
  const MAP = {
    reefer: { Icon: Ship,  bg: 'bg-sky-50',    text: 'text-sky-800'    },
    route:  { Icon: Truck, bg: 'bg-orange-50', text: 'text-orange-700' },
    air:    { Icon: Plane, bg: 'bg-sky-50',    text: 'text-sky-700'    },
  };
  const { Icon, bg, text } = MAP[mode];
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded ${bg} ${text}`}>
        <Icon size={15} strokeWidth={1.75} />
      </span>
      <span className="text-[11px] text-slate-500">{label}</span>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────── */
export default function DesignKitPage() {
  const [activePage, setActivePage] = useState('shipments');
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [drawer2Open, setDrawer2Open] = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [lang, setLang]               = useState('FR');
  const [tablePage, setTablePage]     = useState(1);

  const navItems = [
    { href: '#colors',      label: 'Couleurs'           },
    { href: '#typography',  label: 'Typographie'        },
    { href: '#icons',       label: 'Icônes'             },
    { href: '#buttons',     label: 'Boutons'            },
    { href: '#badges',      label: 'Badges & Statuts'   },
    { href: '#inputs',      label: 'Inputs & Formulaires' },
    { href: '#kpi',         label: 'KPI Cards'          },
    { href: '#cards',       label: 'Data Cards'         },
    { href: '#tables',      label: 'Tableaux'           },
    { href: '#alerts',      label: 'Alertes & Toasts'   },
    { href: '#navigation',  label: 'Navigation'         },
    { href: '#shell',       label: 'App Shell'          },
    { href: '#drawer',      label: 'Drawer'             },
    { href: '#modal',       label: 'Modal'              },
    { href: '#misc',        label: 'Divers'             },
  ];

  return (
    <div className="flex min-h-screen">
      {/* ── Left nav ───────────────────────────────────────── */}
      <aside className="w-60 bg-primary text-white flex flex-col h-screen sticky top-0 z-30 flex-shrink-0">
        <div className="px-5 pt-5 pb-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-12 h-9 rounded-md bg-white grid place-items-center overflow-hidden p-0.5 flex-shrink-0">
              <span className="text-primary font-bold text-[10px] tracking-tighter leading-tight text-center">MFB</span>
            </div>
            <div>
              <div className="font-semibold text-[14px] tracking-tight">Maroc Fruit Board</div>
              <div className="text-[10.5px] text-sky-200">Design Kit v1.0</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-3 overflow-y-auto text-[13px]">
          <div className="text-[10px] uppercase tracking-wider text-sky-300/70 px-2 pt-2 pb-1.5 font-semibold">Fondations</div>
          {navItems.slice(0, 3).map((item) => (
            <a key={item.href} href={item.href}
               className="flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium mb-0.5 transition text-sky-100/80 hover:bg-white/5 hover:text-white">
              {item.label}
            </a>
          ))}
          <div className="text-[10px] uppercase tracking-wider text-sky-300/70 px-2 pt-4 pb-1.5 font-semibold">Composants</div>
          {navItems.slice(3, 10).map((item) => (
            <a key={item.href} href={item.href}
               className="flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium mb-0.5 transition text-sky-100/80 hover:bg-white/5 hover:text-white">
              {item.label}
            </a>
          ))}
          <div className="text-[10px] uppercase tracking-wider text-sky-300/70 px-2 pt-4 pb-1.5 font-semibold">Patterns</div>
          {navItems.slice(10).map((item) => (
            <a key={item.href} href={item.href}
               className="flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium mb-0.5 transition text-sky-100/80 hover:bg-white/5 hover:text-white">
              {item.label}
            </a>
          ))}
        </nav>

        <div className="border-t border-white/10 px-4 py-3 text-[11px] text-sky-300/60">
          Inter · Tailwind CSS · Lucide
        </div>
      </aside>

      {/* ── Main content ───────────────────────────────────── */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        {/* Page header */}
        <div className="bg-white border-b border-slate-200 px-8 py-6 sticky top-0 z-20">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-0.5">MFB Fresh Logistics</div>
              <h1 className="text-[22px] font-semibold tracking-tight text-slate-900">Design Kit</h1>
            </div>
            <div className="flex items-center gap-3 text-[12px] text-slate-500">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-primary inline-block" />Primary #0F3D5C</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-accent inline-block" />Accent #F39200</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-teal-600 inline-block" />Teal #0D9488</span>
            </div>
          </div>
        </div>

        <div className="px-8 py-8 max-w-[1200px] space-y-16">

          {/* ── 01 COLORS ─────────────────────────────────── */}
          <Section id="colors" index="Fondation · 01" title="Couleurs"
            subtitle="Palette sémantique — primaire navy, accent orange, teal pour les actions, états via emerald/amber/rose/sky.">
            <div className="mb-4">
              <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-3">Marque</div>
              <div className="flex flex-wrap gap-3">
                {[
                  { name: 'Primary',       hex: '#0F3D5C', cls: 'bg-primary'    },
                  { name: 'Primary Dark',  hex: '#0A2E47', cls: 'bg-primary-dark' },
                  { name: 'Accent',        hex: '#F39200', cls: 'bg-accent'     },
                  { name: 'Accent Light',  hex: '#FFA94D', cls: 'bg-accent-light' },
                  { name: 'Teal',          hex: 'teal-600', cls: 'bg-teal-600'  },
                ].map((c) => (
                  <div key={c.name} className="overflow-hidden rounded-xl border border-slate-200 w-36">
                    <div className={`h-16 ${c.cls}`} />
                    <div className="bg-white px-3 py-2">
                      <div className="text-[12px] font-semibold text-slate-800">{c.name}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{c.hex}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-3">États sémantiques</div>
              <div className="flex flex-wrap gap-3">
                {[
                  { name: 'Success',  cls: 'bg-emerald-600', tw: 'emerald-600' },
                  { name: 'Warning',  cls: 'bg-amber-500',   tw: 'amber-500'   },
                  { name: 'Danger',   cls: 'bg-rose-600',    tw: 'rose-600'    },
                  { name: 'Info',     cls: 'bg-sky-600',     tw: 'sky-600'     },
                  { name: 'Disputed', cls: 'bg-violet-600',  tw: 'violet-600'  },
                ].map((c) => (
                  <div key={c.name} className="overflow-hidden rounded-xl border border-slate-200 w-32">
                    <div className={`h-12 ${c.cls}`} />
                    <div className="bg-white px-3 py-2">
                      <div className="text-[12px] font-semibold text-slate-800">{c.name}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{c.tw}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* ── 02 TYPOGRAPHY ─────────────────────────────── */}
          <Section id="typography" index="Fondation · 02" title="Typographie"
            subtitle="Inter sur toute l'application. Échelle serrée : les tailles clés sont 11px, 12.5px, 13px, 14px, 24px.">
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              {[
                { label: '24px · font-semibold · tracking-tight', cls: 'text-[24px] font-semibold tracking-tight text-slate-900', sample: 'Tableau de bord' },
                { label: '16px · font-semibold',                  cls: 'text-[16px] font-semibold text-slate-900',                 sample: 'Shipments en cours' },
                { label: '14px · font-semibold',                  cls: 'text-[14px] font-semibold text-slate-900',                 sample: 'INV-2026-04391 · COPAG' },
                { label: '13px · font-normal',                    cls: 'text-[13px] text-slate-700',                               sample: 'Pilotez vos shipments reefer, suivez vos factures et vos sinistres depuis un portail unique.' },
                { label: '12.5px · text-slate-500',               cls: 'text-[12.5px] text-slate-500',                             sample: 'Dernière synchronisation il y a 3 minutes · 847 enregistrements' },
                { label: '11.5px · font-medium',                  cls: 'text-[11.5px] font-medium text-slate-600',                 sample: 'Agadir (MA) → Rotterdam (NL)' },
                { label: '11px · uppercase · tracking-wider',     cls: 'text-[11px] uppercase tracking-wider font-semibold text-slate-500', sample: 'Espace client' },
                { label: '12px · font-mono · tabular-nums',       cls: 'text-[12px] font-mono tabular-nums text-slate-700',       sample: 'MFB-SH-2026-08214 · 28 400 €' },
              ].map((r) => (
                <div key={r.label} className="flex items-baseline gap-6 px-6 py-4">
                  <div className="w-52 flex-shrink-0 text-[11px] text-slate-400 font-mono">{r.label}</div>
                  <span className={r.cls}>{r.sample}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* ── 03 ICONS ──────────────────────────────────── */}
          <Section id="icons" index="Fondation · 03" title="Icônes"
            subtitle="Lucide · strokeWidth 1.75 · taille standard 16px. Les icônes de transport ont leurs propres conteneurs colorés.">
            <SectionCard title="Icônes courantes (16px · strokeWidth 1.75)">
              <div className="flex flex-wrap gap-3 mb-6">
                {[
                  Ship, Truck, Plane, Receipt, ShieldAlert, AlertOctagon, Tag, BarChart3,
                  BookOpen, MessageSquare, Bell, Search, Download, CreditCard, CheckCircle2,
                  AlertTriangle, Clock, Calendar, LogOut, Plus, X, ChevronRight, ArrowUp, ArrowDown, Wallet, Scale,
                ].map((Icon, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-slate-100 transition w-16">
                    <Icon size={16} strokeWidth={1.75} className="text-slate-600" />
                    <span className="text-[9px] text-slate-400 text-center leading-tight">{Icon.displayName || Icon.name}</span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-3">Transport mode icons</div>
              <div className="flex gap-3">
                <TransportIcon mode="reefer" label="Reefer" />
                <TransportIcon mode="route"  label="Route"  />
                <TransportIcon mode="air"    label="Aérien" />
              </div>
            </SectionCard>
          </Section>

          {/* ── 04 BUTTONS ────────────────────────────────── */}
          <Section id="buttons" index="Composant · 04" title="Boutons"
            subtitle="Trois variants principaux, deux tailles, états désactivé et chargement.">
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              <div className="px-6 py-5">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-4">Variants principaux</div>
                <div className="flex flex-wrap gap-3 items-center">
                  <Button variant="primary" icon={Ship}>Nouveau shipment</Button>
                  <Button variant="accent"  icon={Plus}>Demande de cotation</Button>
                  <Button variant="secondary" icon={Download}>Export comptable</Button>
                  <Button variant="danger" icon={X}>Supprimer</Button>
                </div>
              </div>
              <div className="px-6 py-5">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-4">Tailles</div>
                <div className="flex flex-wrap gap-3 items-center">
                  <Button variant="primary" size="sm">Petit</Button>
                  <Button variant="primary" size="md">Standard</Button>
                  <Button variant="primary" size="lg">Grand</Button>
                </div>
              </div>
              <div className="px-6 py-5">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-4">États</div>
                <div className="flex flex-wrap gap-3 items-center">
                  <Button variant="primary" disabled>Désactivé</Button>
                  <Button variant="primary" loading>Synchronisation ERP…</Button>
                  <IconButton icon={Search} variant="secondary" />
                  <IconButton icon={Scale} variant="secondary" />
                </div>
              </div>
            </div>
          </Section>

          {/* ── 05 BADGES ─────────────────────────────────── */}
          <Section id="badges" index="Composant · 05" title="Badges & Statuts"
            subtitle="Badges rounded-full avec dot de couleur et ring-1 pour les contours.">
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              <div className="px-6 py-5">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-4">Statuts Shipments</div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="preparing">En préparation</Badge>
                  <Badge variant="transit">En transit</Badge>
                  <Badge variant="delivered">Livré</Badge>
                  <Badge variant="incident">Incident</Badge>
                </div>
              </div>
              <div className="px-6 py-5">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-4">Statuts Factures</div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="pending">À régler</Badge>
                  <Badge variant="paid">Payée</Badge>
                  <Badge variant="overdue30">Échue 1–30j</Badge>
                  <Badge variant="overdue60">Échue +30j</Badge>
                  <Badge variant="disputed">Contestée</Badge>
                </div>
              </div>
              <div className="px-6 py-5">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-4">Statuts Sinistres & Réclamations</div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="investigation">Investigation</Badge>
                  <Badge variant="expertise">Expertise</Badge>
                  <Badge variant="indemnified">Indemnisé</Badge>
                  <Badge variant="rejected">Rejeté</Badge>
                  <Badge variant="ongoing">En cours</Badge>
                  <Badge variant="resolved">Résolu</Badge>
                </div>
              </div>
              <div className="px-6 py-5">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-4">Tier clients & badges génériques</div>
                <div className="flex flex-wrap gap-2 items-center">
                  <TierBadge tier="bronze">Bronze</TierBadge>
                  <TierBadge tier="silver">Silver</TierBadge>
                  <TierBadge tier="gold">Gold</TierBadge>
                  <TierBadge tier="platinum">Platinum</TierBadge>
                  <span className="ml-2"><CountBadge count={3} /></span>
                  <CountBadge count="!" variant="danger" />
                </div>
              </div>
            </div>
          </Section>

          {/* ── 06 INPUTS ─────────────────────────────────── */}
          <Section id="inputs" index="Composant · 06" title="Inputs & Formulaires"
            subtitle="bg-white · border-slate-200 · h-10 · focus:border-teal-600. Labels en uppercase 11px.">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="grid grid-cols-2 gap-5 mb-6">
                <FormField label="Référence shipment" required helper="Numéro ERP · Sales.Document No_">
                  <Input leadingIcon={Search} defaultValue="MFB-SH-2026-08214" />
                </FormField>
                <FormField label="Mode de transport">
                  <Select>
                    <option>Reefer (maritime)</option>
                    <option>Route</option>
                    <option>Aérien</option>
                  </Select>
                </FormField>
                <FormField label="Champ avec erreur" error="Format de référence non reconnu" helper="Format de référence non reconnu">
                  <Input state="error" defaultValue="REF-INVALIDE" />
                </FormField>
                <FormField label="Champ validé" success="Trouvé dans l'ERP" helper="Trouvé dans l'ERP">
                  <Input state="success" defaultValue="INV-2026-04391" trailingIcon={CheckCircle2} />
                </FormField>
                <div className="col-span-2">
                  <FormField label="Commentaire sinistre">
                    <Textarea defaultValue="Rupture de chaîne du froid détectée lors de l'escale de Rotterdam. Alarme reefer déclenchée à +6°C pendant environ 4 heures." />
                  </FormField>
                </div>
              </div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-3">Contrôles</div>
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" defaultChecked className="w-4 h-4 accent-teal-600" />
                  <span className="text-[13px] text-slate-700">Réclamation recevable</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-teal-600" />
                  <span className="text-[13px] text-slate-700">Expertise réalisée</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="mode2" defaultChecked className="w-4 h-4 accent-teal-600" />
                  <span className="text-[13px] text-slate-700">Reefer</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="mode2" className="w-4 h-4 accent-teal-600" />
                  <span className="text-[13px] text-slate-700">Route</span>
                </label>
                <div className="flex items-center gap-2">
                  <ToggleGroup
                    options={[{ value: 'FR', label: 'FR' }, { value: 'EN', label: 'EN' }]}
                    value={lang}
                    onChange={setLang}
                  />
                  <span className="text-[12px] text-slate-500">Toggle langue</span>
                </div>
              </div>
            </div>
          </Section>

          {/* ── 07 KPI CARDS ──────────────────────────────── */}
          <Section id="kpi" index="Composant · 07" title="KPI Cards"
            subtitle="bg-white · border · rounded-xl · p-4. Sparkline SVG en position absolue en bas à droite.">
            <div className="grid grid-cols-4 gap-4">
              <KpiCard
                label="Chiffre d'affaires YTD"
                icon={TrendingUp}
                value="€ 1 284 200"
                trend="up"
                trendLabel="+18% vs N-1"
                sparkline={[25.7, 18.3, 22.5, 14.0, 18.3, 12.1, 8.4, 2.0]}
              />
              <KpiCard
                label="Shipments en cours"
                icon={Ship}
                value="8 shipments"
                sub="dont 1 incident actif"
              />
              <KpiCard
                label="Encours échu"
                icon={AlertTriangle}
                value="€ 87 200"
                trend="down"
                trendLabel="Action requise"
              />
              <KpiCard
                label="Ponctualité"
                icon={Target}
                value="94.1%"
                trend="up"
                trendLabel="+1.3 pts vs M-1"
              />
            </div>
          </Section>

          {/* ── 08 DATA CARDS ─────────────────────────────── */}
          <Section id="cards" index="Composant · 08" title="Data Cards"
            subtitle="Cartes shipment avec barre de progression, cartes facture, et carte générique avec header/footer.">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <ShipmentCard
                reference="MFB-SH-2026-08214"
                product="Oranges Maroc Late"
                origin="Agadir (MA)"
                destination="Rotterdam (NL)"
                weight="24 t"
                status="transit"
                progress={58}
                eta="22 avr."
                temperature="+4°C"
                container="MSCU-7741821"
                mode="reefer"
              />
              <ShipmentCard
                reference="MFB-SH-2026-08210"
                product="Oranges Maroc Late"
                origin="Alexandrie (EG)"
                destination="Saint-Pétersbourg"
                status="incident"
                mode="reefer"
                alertMessage="Rupture chaîne du froid signalée"
              />
            </div>
            <Card
              title="Factures récentes"
              headerRight={<Badge variant="pending">3 en attente</Badge>}
              footer={
                <div className="flex gap-2 justify-end">
                  <Button variant="secondary" size="sm">Voir tout</Button>
                  <Button variant="primary"   size="sm">Export</Button>
                </div>
              }
              className="max-w-md"
            >
              <CardRow
                icon={Receipt}
                title="INV-2026-04391"
                subtitle="COPAG · Agadir"
                valueMain="28 600 €"
                valueSub="Éch. 14 juin"
              />
              <CardRow
                icon={Receipt}
                title="INV-2026-04348"
                subtitle="Karoo Grapes"
                valueMain="18 200 €"
                valueSub="Échue 12j"
                valueDanger
              />
            </Card>
          </Section>

          {/* ── 09 TABLES ─────────────────────────────────── */}
          <Section id="tables" index="Composant · 09" title="Tableaux"
            subtitle="bg-white · border · rounded-xl · header bg-slate-50 · 11px uppercase. Hover sur les lignes. Actions alignées à droite.">
            <ShipmentTable
              rows={SHIPMENT_ROWS}
              page={tablePage}
              total={47}
              onPrev={() => setTablePage((p) => Math.max(1, p - 1))}
              onNext={() => setTablePage((p) => p + 1)}
            />
          </Section>

          {/* ── 10 ALERTS ─────────────────────────────────── */}
          <Section id="alerts" index="Composant · 10" title="Alertes & Toasts"
            subtitle="Bannières inline et toasts WebSocket. Même palette sémantique que les badges.">
            <div className="space-y-3 mb-6 max-w-xl">
              <Alert variant="success" title="Sync ERP terminée" dismissible>
                847 enregistrements mis à jour depuis Dynamics BC · il y a 2 min
              </Alert>
              <Alert variant="info" title="Nouveaux champs disponibles">
                La facture inclut désormais fretCurrencyCode depuis la mise à jour Dynamics BC
              </Alert>
              <Alert variant="warning" title="3 admissions temporaires expirent dans 15 jours">
                AT-2026-0142, AT-2026-0138, AT-2026-0127 nécessitent une action urgente
              </Alert>
              <Alert variant="danger" title="Facture échue +60 jours">
                INV-2026-03841 · COPAG · 38 200 € · 67 jours de retard
              </Alert>
            </div>
            <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-3">Toasts (push WebSocket)</div>
            <div className="flex flex-col gap-2 max-w-xs">
              <Toast variant="success" title="Shipment livré" body="MFB-SH-2026-08208 · Calais ✓" />
              <Toast variant="warning" title="AT expire dans 12 jours" body="AT-2026-0142 · Échéance UDG proche" />
            </div>
          </Section>

          {/* ── 11 NAVIGATION ─────────────────────────────── */}
          <Section id="navigation" index="Pattern · 11" title="Navigation"
            subtitle="Tabs border-b avec indicateur accent orange ou teal. Breadcrumb minimaliste. Toggle langue.">
            <div className="space-y-4">
              {/* Tab bar */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="border-b border-slate-200 px-4 flex items-center gap-1">
                  {['Grilles tarifaires (12)', 'Historique cotations (8)', 'Workflow validation (3)'].map((tab, i) => (
                    <button key={tab} className={`px-3 h-11 text-[13px] font-semibold border-b-2 transition ${i === 0 ? 'border-accent text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                      {tab}
                    </button>
                  ))}
                </div>
                <div className="px-4 py-3 text-[12.5px] text-slate-400">Contenu de l'onglet actif…</div>
              </div>

              {/* Topbar snippet */}
              <div className="bg-white border border-slate-200 rounded-xl px-5 py-3 flex items-center gap-4">
                <div>
                  <div className="text-[11px] text-slate-500">Portail client · Shipments</div>
                  <div className="text-[14px] font-semibold tracking-tight">MFB-SH-2026-08214</div>
                </div>
                <div className="flex-1 max-w-sm relative">
                  <Search size={13} strokeWidth={1.75} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    className="w-full bg-slate-50 border border-slate-200 rounded-md h-8 pl-8 pr-14 text-[12.5px] outline-none focus:border-teal-600"
                    placeholder="Rechercher un shipment, facture, client…"
                  />
                  <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-500 font-mono">⌘K</kbd>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <select className="bg-white border border-slate-200 rounded-md h-8 px-2 text-[11.5px] font-medium">
                    <option>Domaines Agricoles</option>
                  </select>
                  <ToggleGroup
                    options={[{ value: 'FR', label: 'FR' }, { value: 'EN', label: 'EN' }]}
                    value={lang}
                    onChange={setLang}
                  />
                  <button className="w-8 h-8 grid place-items-center text-slate-500 hover:bg-slate-100 rounded-md relative transition">
                    <Bell size={16} strokeWidth={1.75} />
                    <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-accent ring-2 ring-white" />
                  </button>
                </div>
              </div>
            </div>
          </Section>

          {/* ── 12 APP SHELL ──────────────────────────────── */}
          <Section id="shell" index="Pattern · 12" title="App Shell"
            subtitle="Sidebar navy #0F3D5C · w-60 · sticky. Topbar bg-white · h-14 · sticky top-0. Contenu scrollable.">
            <div className="border border-slate-200 rounded-xl overflow-hidden" style={{ height: 340 }}>
              <div className="flex h-full">
                <div className="w-44 bg-primary text-white flex flex-col flex-shrink-0">
                  <div className="px-3 pt-3 pb-2.5 border-b border-white/10 flex items-center gap-2">
                    <div className="w-9 h-7 rounded bg-white grid place-items-center overflow-hidden p-0.5 flex-shrink-0">
                      <span className="text-primary font-bold text-[9px]">MFB</span>
                    </div>
                    <div className="text-[11px] font-semibold leading-tight">
                      Maroc Fruit Board<br />
                      <span className="text-sky-200 font-normal">Fresh Logistics</span>
                    </div>
                  </div>
                  <nav className="px-2 py-2 text-[11.5px]">
                    <div className="text-[9px] uppercase tracking-wider text-sky-300/70 px-2 py-1.5 font-semibold">Espace client</div>
                    {[
                      { key: 'shipments', label: 'Shipments',   Icon: Ship         },
                      { key: 'invoices',  label: 'Facturation', Icon: Receipt      },
                      { key: 'claims',    label: 'Sinistres',   Icon: ShieldAlert  },
                      { key: 'messages',  label: 'Messagerie',  Icon: MessageSquare, badge: 2 },
                    ].map(({ key, label, Icon, badge }) => (
                      <button
                        key={key}
                        onClick={() => setActivePage(key)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded mb-0.5 transition text-left ${activePage === key ? 'bg-white/15 text-white font-medium' : 'text-sky-100/70 hover:bg-white/5'}`}
                      >
                        <Icon size={13} strokeWidth={1.75} />
                        <span className="flex-1">{label}</span>
                        {badge && <span className="bg-accent text-white text-[9px] font-bold px-1 py-0.5 rounded-full">{badge}</span>}
                      </button>
                    ))}
                  </nav>
                </div>
                <div className="flex-1 flex flex-col bg-slate-50">
                  <div className="h-11 bg-white border-b border-slate-200 flex items-center px-4 gap-3">
                    <div>
                      <div className="text-[9px] text-slate-400">Portail · Shipments</div>
                      <div className="text-[12px] font-semibold">Tableau de bord</div>
                    </div>
                    <div className="flex-1 bg-slate-50 border border-slate-200 rounded h-6 ml-4" />
                    <button className="w-6 h-6 rounded bg-slate-100 text-slate-500 grid place-items-center">
                      <Bell size={12} strokeWidth={1.75} />
                    </button>
                  </div>
                  <div className="flex-1 p-3 grid grid-cols-3 gap-2 content-start">
                    <div className="bg-white border border-slate-200 rounded-lg p-3">
                      <div className="text-[9px] text-slate-400 mb-1">Shipments actifs</div>
                      <div className="text-[18px] font-semibold text-slate-900">147</div>
                      <div className="text-[9px] text-emerald-600">↑ 12%</div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-3">
                      <div className="text-[9px] text-slate-400 mb-1">Échu</div>
                      <div className="text-[18px] font-semibold text-rose-600">€218k</div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-3">
                      <div className="text-[9px] text-slate-400 mb-1">Ponctualité</div>
                      <div className="text-[18px] font-semibold text-slate-900">94.1%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* ── 13 DRAWER ─────────────────────────────────── */}
          <Section id="drawer" index="Pattern · 13" title="Drawer (panneau latéral)"
            subtitle="Glisse depuis la droite · bg-slate-50 · border-l · shadow-2xl · backdrop-blur. Utilisé pour les détails shipment, facture, sinistre.">
            <div className="flex gap-3 mb-5">
              <Button variant="primary" onClick={() => setDrawerOpen(true)}>Ouvrir drawer shipment</Button>
              <Button variant="secondary" onClick={() => setDrawer2Open(true)}>Ouvrir drawer sinistre</Button>
            </div>
            {/* Static preview */}
            <div className="border border-slate-200 rounded-xl overflow-hidden relative bg-slate-100" style={{ height: 280 }}>
              <div className="absolute inset-0 flex items-center justify-center text-slate-300 text-[13px]">← zone contenu principal</div>
              <div className="absolute top-0 right-0 bottom-0 w-72 bg-slate-50 border-l border-slate-200 shadow-2xl flex flex-col">
                <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-2">
                  <div className="flex-1">
                    <div className="text-[11px] text-slate-400 font-mono">MFB-SH-2026-08214</div>
                    <div className="text-[13px] font-semibold text-slate-900">Oranges Maroc Late</div>
                  </div>
                  <Badge variant="transit">En transit</Badge>
                  <button className="w-7 h-7 grid place-items-center text-slate-400 hover:bg-slate-100 rounded">
                    <X size={13} />
                  </button>
                </div>
                <div className="flex-1 p-4 overflow-y-auto space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-[12px]">
                    {[['Origine', 'Agadir (MA)'], ['Destination', 'Rotterdam (NL)'], ['Quantité', '24 t · +4°C'], ['Conteneur', 'MSCU-7741821']].map(([l, v]) => (
                      <div key={l}>
                        <div className="text-slate-400 text-[10.5px] uppercase font-medium">{l}</div>
                        <div className="font-medium text-slate-800 mt-0.5">{v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="h-1.5 bg-slate-200 rounded-full">
                    <div className="h-full bg-teal-600 rounded-full" style={{ width: '58%' }} />
                  </div>
                </div>
                <div className="px-4 py-2.5 border-t border-slate-200 bg-white flex gap-2">
                  <Button variant="secondary" size="sm" className="flex-1">Documents</Button>
                  <Button variant="primary"   size="sm" className="flex-1">Déclarer sinistre</Button>
                </div>
              </div>
            </div>
          </Section>

          {/* ── 14 MODAL ──────────────────────────────────── */}
          <Section id="modal" index="Pattern · 14" title="Modal"
            subtitle="Overlay backdrop-blur · modal bg-white · rounded-2xl · shadow-2xl · animation slideUp.">
            <Button variant="accent" onClick={() => setModalOpen(true)} className="mb-5">
              Ouvrir modal déclaration sinistre
            </Button>
            {/* Static preview */}
            <div className="border border-slate-200 rounded-xl overflow-hidden relative bg-slate-900/5" style={{ height: 320 }}>
              <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px]" />
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-[15px] font-semibold text-slate-900">Déclarer un sinistre</span>
                    <button className="w-7 h-7 grid place-items-center text-slate-400 hover:bg-slate-100 rounded text-[12px]">
                      <X size={13} />
                    </button>
                  </div>
                  <div className="px-6 py-4 space-y-3">
                    <FormField label="Référence shipment">
                      <Input defaultValue="MFB-SH-2026-08210" readOnly />
                    </FormField>
                    <FormField label="Cause du sinistre">
                      <Select><option>Rupture chaîne du froid</option></Select>
                    </FormField>
                  </div>
                  <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                    <Button variant="secondary" size="sm">Annuler</Button>
                    <Button variant="primary"   size="sm">Soumettre</Button>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* ── 15 MISC ───────────────────────────────────── */}
          <Section id="misc" index="Pattern · 15" title="Divers"
            subtitle="Avatars, timeline sinistre, état vide, Olive le chatbot.">
            <div className="grid grid-cols-2 gap-4">
              {/* Avatars */}
              <SectionCard title="Avatars">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar initials="BH" size="sm" />
                  <Avatar initials="AC" size="md" />
                  <Avatar initials="KM" size="lg" />
                </div>
                <div className="flex items-center gap-3">
                  <AvatarGroup avatars={['BH', 'AC', 'KM', 'FZ', 'MN', 'YB', 'SA']} max={4} />
                  <span className="text-[12px] text-slate-500">Groupe d'avatars</span>
                </div>
              </SectionCard>

              {/* Olive chatbot */}
              <SectionCard title="Chatbot FAB · Olive">
                <div className="relative inline-block">
                  <div className="w-12 h-12 rounded-full bg-primary grid place-items-center shadow-lg cursor-pointer hover:bg-primary-dark transition">
                    <span className="text-[22px]">🫒</span>
                  </div>
                  <span className="absolute bottom-0.5 right-0.5 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-white" />
                </div>
                <p className="text-[12px] text-slate-500 mt-3">Olive — assistante MFB. Toujours visible en bas à droite.</p>
              </SectionCard>

              {/* Timeline */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 col-span-2">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-4">Timeline sinistre</div>
                <Timeline items={TIMELINE_ITEMS} />
              </div>

              {/* Empty state */}
              <div className="bg-white border border-slate-200 rounded-xl p-8 flex flex-col items-center justify-center text-center col-span-2">
                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 grid place-items-center text-[22px] mb-4">📦</div>
                <div className="text-[15px] font-semibold text-slate-900 mb-1.5">Aucun shipment trouvé</div>
                <div className="text-[13px] text-slate-500 max-w-xs mb-4">
                  Aucun résultat ne correspond à vos filtres actuels. Essayez d'ajuster la plage de dates ou le statut.
                </div>
                <Button variant="secondary">Réinitialiser les filtres</Button>
              </div>
            </div>
          </Section>

        </div>
      </main>

      {/* ── Live Drawer: Shipment ──────────────────────────── */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        subtitle="MFB-SH-2026-08214"
        title="Oranges Maroc Late · 24 t"
        headerRight={<Badge variant="transit">En transit</Badge>}
        footer={
          <>
            <Button variant="secondary" className="flex-1" onClick={() => setDrawerOpen(false)}>Fermer</Button>
            <Button variant="primary"   className="flex-1">Déclarer un sinistre</Button>
          </>
        }
      >
        <DrawerDetailGrid fields={[
          { label: 'Origine',      value: 'Agadir (MA)'   },
          { label: 'Destination',  value: 'Rotterdam (NL)' },
          { label: 'ETD',          value: '22 avr. 2026'  },
          { label: 'Conteneur',    value: 'MSCU-7741821',  mono: true },
          { label: 'Température',  value: '+4°C'          },
          { label: 'Incoterm',     value: 'CIF'           },
        ]} />
        <DrawerProgressBar percent={58} leftLabel="Départ Agadir" rightLabel="58% · ETA 29 avr." />
      </Drawer>

      {/* ── Live Drawer: Sinistre ─────────────────────────── */}
      <Drawer
        open={drawer2Open}
        onClose={() => setDrawer2Open(false)}
        subtitle="SIN-2026-0058"
        title="Rupture chaîne du froid"
        width="w-[480px]"
        headerRight={<Badge variant="investigation">Investigation</Badge>}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDrawer2Open(false)}>Fermer</Button>
            <Button variant="accent" className="ml-auto">Avancer le dossier</Button>
          </>
        }
      >
        <DrawerDetailGrid fields={[
          { label: 'Client',           value: 'Nile Delta Produce' },
          { label: 'Montant réclamé',  value: '24 800 €'          },
          { label: 'Assureur',         value: 'Allianz Trade'     },
          { label: 'Expertise',        value: 'Non réalisée'      },
        ]} />
      </Drawer>

      {/* ── Live Modal ───────────────────────────────────── */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Déclarer un sinistre"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Annuler</Button>
            <Button variant="primary">✓ Soumettre le sinistre</Button>
          </>
        }
      >
        <FormField label="Référence shipment" required>
          <Input defaultValue="MFB-SH-2026-08210" />
        </FormField>
        <FormField label="Cause du sinistre" required>
          <Select>
            <option>Rupture chaîne du froid</option>
            <option>Dommage physique</option>
            <option>Vol</option>
            <option>Retard douane</option>
          </Select>
        </FormField>
        <FormField label="Montant réclamé (EUR)">
          <Input type="number" placeholder="0.00" />
        </FormField>
        <FormField label="Commentaire">
          <Textarea placeholder="Décrivez l'incident…" />
        </FormField>
      </Modal>
    </div>
  );
}
